/* global DetectUserAgent */
import React, { useState } from "react";
import clsx from "clsx";
import { isMobileOnly } from "react-device-detect";
import R from "ramda";
import { List, ListItem, ListItemAvatar, ListItemText, Dialog, DialogTitle, Slide } from "@mui/material";

import SpinningCloseButton from "components/SpinningCloseButton";
import { FACE_PAGE_FLOWS } from "enums";
import { useFaces, useRouter, useUsers, useFilestack, useNotifications } from "hooks";
import { appRoutes } from "routes";
import { rotateAndScale } from "utils/imageUtils";
import browsePic from "assets/images/face-upload/browse.png";
import webcamPic from "assets/images/face-upload/webcam.png";
import facebookPic from "assets/images/face-upload/facebook.png";
import { relationToPronoun } from "utils/relativeUtils";
import { trackEventHeadUploadSuccess, trackEventUploadImageClick } from "utils/gAnalyticsUtils";
import { jjLogger } from "utils/logUtils";

import Webcam from "./components/Webcam";
import useStyles from "./useStyles";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FaceUploadModal = () => {
  const classes = useStyles();
  const [isWebcamVisible, setIsWebcamVisible] = useState(false);
  const { replaceWithSave } = useRouter();
  const { currentUser } = useUsers();
  const { setHandleViewStorage } = useFilestack();
  const { addErrorNotification } = useNotifications();

  const {
    photo,
    setPhoto,
    closeModal,
    clearPhoto,
    clearFoundFaces,
    resetAdded,
    setMode,
    mode,
    relationToCreate,
    clearRelationToCreate,
    clearFacesToAddPerson,
  } = useFaces();
  const goToFace = () => replaceWithSave(appRoutes.facePath());

  const cleanData = () => {
    clearPhoto();
    clearFoundFaces();
    resetAdded();
    clearFacesToAddPerson();
  };

  const prepareAndUpdatePhoto = (base64, newMode) => {
    cleanData();
    setPhoto(base64);
    setMode(newMode);
    goToFace();
    closeModal();
  };

  const setAndDetectPhoto = (base64) => {
    prepareAndUpdatePhoto(base64, FACE_PAGE_FLOWS.LOADING);
  };

  const handleUseLastPhoto = (base64) => {
    prepareAndUpdatePhoto(base64, FACE_PAGE_FLOWS.EDIT);
  };

  const handleCloseModal = () => {
    if (mode !== FACE_PAGE_FLOWS.FOUND_EVERYONE) clearRelationToCreate();
    closeModal();
  };

  const handleOptionClick = (type) => trackEventUploadImageClick(type, currentUser);

  const handleWebcamClick = () => {
    setIsWebcamVisible(true);
    handleOptionClick("Webcam");
  };

  const handleBrowsePhoto = ({ target: { files } }) => {
    trackEventHeadUploadSuccess("Device", currentUser);
    rotateAndScale(R.head(files))
      .then(setAndDetectPhoto)
      .catch((error) => {
        const message = "This file type is not supported, please choose a .JPG, .PNG or .GIF image";
        addErrorNotification(message);
        jjLogger.logError(error);
      });
  };

  const handleTakePhoto = ({ target: { files } }) => {
    trackEventHeadUploadSuccess("Device", currentUser);
    rotateAndScale(R.head(files)).then(setAndDetectPhoto);
  };

  const handleWebcamCapture = (url) => {
    trackEventHeadUploadSuccess("Webcam", currentUser);
    setAndDetectPhoto(url);
    setIsWebcamVisible(false);
  };

  const renderTitle = () => {
    if (mode === FACE_PAGE_FLOWS.FOUND_EVERYONE) {
      return <DialogTitle classes={{ root: classes.title }}>Add Another Face</DialogTitle>;
    }
    return (
      <DialogTitle classes={{ root: classes.title }}>
        {R.isEmpty(relationToCreate)
          ? "Start with a Face Photo!"
          : `Upload a photo of ${relationToPronoun(relationToCreate)}`}
      </DialogTitle>
    );
  };

  const renderModalContent = () => (
    <>
      {renderTitle()}
      <List>
        {photo && (
          <ListItem
            classes={{ root: classes.listItem }}
            divider
            button
            onClick={() => {
              handleOptionClick("Use Last Photo");
              handleUseLastPhoto(photo);
            }}
          >
            <ListItemText disableTypography classes={{ root: classes.listItemText }}>
              Use Last Photo
            </ListItemText>
            <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
              <div className={classes.lastPhoto}>
                <img src={photo} alt="Last" className={classes.lastPhotoImage} />
              </div>
            </ListItemAvatar>
          </ListItem>
        )}

        <label htmlFor="browse-photo-input">
          <ListItem
            classes={{ root: classes.listItem }}
            divider
            button
            onClick={() => handleOptionClick("Browse Photos")}
          >
            <ListItemText disableTypography classes={{ root: classes.listItemText }}>
              Browse Photos
            </ListItemText>
            <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
              <img src={browsePic} alt="Browse Photos" className={classes.listItemIcon} />
            </ListItemAvatar>
          </ListItem>
          <input
            accept="image/*"
            className={classes.inputFile}
            id="browse-photo-input"
            type="file"
            onChange={handleBrowsePhoto}
          />
        </label>

        {!DetectUserAgent.isFB4Android() && (
          <>
            {isMobileOnly ? (
              <label htmlFor="take-photo-input">
                <ListItem
                  classes={{ root: classes.listItem }}
                  divider
                  button
                  onClick={() => handleOptionClick("Take Photo")}
                >
                  <ListItemText disableTypography classes={{ root: classes.listItemText }}>
                    Take Photo
                  </ListItemText>
                  <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
                    <img src={webcamPic} alt="Webcam" className={classes.listItemIcon} />
                  </ListItemAvatar>
                </ListItem>
                <input
                  accept="image/*"
                  className={classes.inputFile}
                  id="take-photo-input"
                  type="file"
                  onChange={handleTakePhoto}
                  capture="user"
                />
              </label>
            ) : (
              <ListItem classes={{ root: classes.listItem }} divider button onClick={handleWebcamClick}>
                <ListItemText disableTypography classes={{ root: classes.listItemText }}>
                  Webcam
                </ListItemText>
                <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
                  <img src={webcamPic} alt="Webcam" className={classes.listItemIcon} />
                </ListItemAvatar>
              </ListItem>
            )}
          </>
        )}

        <ListItem
          onClick={() => {
            handleOptionClick("Facebook");
            setHandleViewStorage(true);
          }}
          classes={{ root: classes.listItem }}
          button
        >
          <ListItemText disableTypography classes={{ root: classes.listItemText }}>
            Facebook
          </ListItemText>
          <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
            <img
              src={facebookPic}
              alt="Facebook"
              className={clsx(classes.listItemIcon, classes.listItemIconFacebook)}
            />
          </ListItemAvatar>
        </ListItem>
      </List>
    </>
  );

  return (
    <Dialog
      classes={{ root: classes.root }}
      PaperProps={{ classes: { root: classes.paper } }}
      open
      TransitionComponent={Transition}
    >
      <SpinningCloseButton onClick={handleCloseModal} className={classes.buttonClose} />
      {isWebcamVisible ? <Webcam onCapture={handleWebcamCapture} /> : renderModalContent()}
    </Dialog>
  );
};

export default FaceUploadModal;
